import { FieldType, FieldTypeConfig, PlacedField, QuickSendField } from "../types";

export const FIELD_TYPE_CONFIGS: Record<FieldType, FieldTypeConfig> = {
  signature: { label: "Signature", defaultWidth: 20, defaultHeight: 4 },
  initials: { label: "Initials", defaultWidth: 8, defaultHeight: 4 },
  approval_stamp: { label: "Approval Stamp", defaultWidth: 15, defaultHeight: 6 },
  full_name: { label: "Full Name", defaultWidth: 18, defaultHeight: 3 },
  email: { label: "Email", defaultWidth: 18, defaultHeight: 3 },
  date_signed: { label: "Date Signed", defaultWidth: 12, defaultHeight: 3 },
  text: { label: "Text", defaultWidth: 18, defaultHeight: 3 },
  date: { label: "Date", defaultWidth: 12, defaultHeight: 3 },
  checkbox: { label: "Checkbox", defaultWidth: 3, defaultHeight: 2.5 },
};

export const RECIPIENT_COLORS = ["#4A90D9", "#C060A0", "#5E9B5E", "#C48550", "#9B82C3", "#4A9A9C", "#C96B6B"];

const TAG_PREFIX = "ddsign-field::";

export async function insertFieldContentControl(field: PlacedField, recipientName: string): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const config = FIELD_TYPE_CONFIGS[field.type];
    const label = `[${config.label} - ${recipientName}]`;

    const cc = selection.insertContentControl();
    cc.tag = `${TAG_PREFIX}${field.id}`;
    cc.title = `${config.label} (${recipientName})`;
    cc.appearance = "BoundingBox";
    cc.color = RECIPIENT_COLORS[field.recipientIndex % RECIPIENT_COLORS.length];

    // Insert placeholder text first, then lock editing
    cc.insertText(label, "Replace");
    await context.sync();

    cc.cannotEdit = true;
    await context.sync();
  });
}

export async function removeFieldContentControl(fieldId: string): Promise<void> {
  await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("items/tag");
    await context.sync();

    const tag = `${TAG_PREFIX}${fieldId}`;
    for (const cc of contentControls.items) {
      if (cc.tag === tag) {
        cc.delete(false);
        break;
      }
    }

    await context.sync();
  });
}

export async function removeAllFieldContentControls(fields: PlacedField[]): Promise<void> {
  await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("items/tag");
    await context.sync();

    const tags = new Set(fields.map((f) => `${TAG_PREFIX}${f.id}`));

    for (const cc of contentControls.items) {
      if (cc.tag.startsWith(TAG_PREFIX) || tags.has(cc.tag)) {
        cc.delete(false);
      }
    }

    await context.sync();
  });
}

export async function estimateFieldPositions(fields: PlacedField[]): Promise<QuickSendField[]> {
  return await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("items/tag,items/font");
    await context.sync();

    // Build a map of tag → content control
    const ccMap = new Map<string, Word.ContentControl>();
    for (const cc of contentControls.items) {
      ccMap.set(cc.tag, cc);
    }

    // Get all paragraphs to estimate page positions
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const totalParagraphs = paragraphs.items.length;
    const paragraphsPerPage = 45; // heuristic
    const totalPages = Math.max(1, Math.ceil(totalParagraphs / paragraphsPerPage));

    // For each field, find its content control and estimate position
    const result: QuickSendField[] = [];

    for (const field of fields) {
      const tag = `${TAG_PREFIX}${field.id}`;
      const cc = ccMap.get(tag);
      const config = FIELD_TYPE_CONFIGS[field.type];

      let pageNumber = 1;
      let yPosition = 50; // default to middle

      if (cc) {
        // Find which paragraph contains this content control
        const ccRange = cc.getRange("Start");

        try {
          // Use compareLocationWith to find position relative to paragraphs
          const compResults: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];

          for (let i = 0; i < paragraphs.items.length; i++) {
            const pRange = paragraphs.items[i].getRange("Start");
            compResults.push(ccRange.compareLocationWith(pRange));
          }

          await context.sync();

          // Find the paragraph closest to the content control
          let paragraphIndex = 0;
          for (let i = 0; i < compResults.length; i++) {
            const rel = compResults[i].value;
            if (rel === "Before" || rel === "Equal") {
              paragraphIndex = i;
              break;
            }
            if (rel === "After" || rel === "Contains") {
              paragraphIndex = i;
            }
          }

          pageNumber = Math.min(totalPages, Math.floor(paragraphIndex / paragraphsPerPage) + 1);
          const posInPage = (paragraphIndex % paragraphsPerPage) / paragraphsPerPage;
          yPosition = Math.min(95, Math.max(2, posInPage * 100));
        } catch {
          // Fallback: use default position
        }
      }

      result.push({
        name: field.name,
        type: field.type,
        recipient_index: field.recipientIndex,
        page_number: pageNumber,
        x_position: 30,
        y_position: Math.round(yPosition * 100) / 100,
        width: config.defaultWidth,
        height: config.defaultHeight,
        is_required: true,
      });
    }

    return result;
  });
}
